import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Message } from 'src/app/shared/models/domain';
import { Voucher, VoucherEntry } from 'src/app/shared/models/finance';
import { CommonService } from 'src/app/shared/services/common.service';
import { FinanceService } from 'src/app/shared/services/finance.service';
import { VoucherEntriesComponent } from '../voucher-entries/voucher-entries.component';
import { RegExp } from 'src/app/core/config/regex';

@Component({
  selector: 'app-manage-voucher',
  templateUrl: './manage-voucher.component.html',
  styleUrls: ['./manage-voucher.component.scss']
})
export class ManageVoucherComponent implements OnInit {
  @Output() passEntry: EventEmitter<any> = new EventEmitter();
  @Input() voucher: Voucher;
  @Input() vouchers: Voucher[] = [];
  public voucherTypes: string[] = [];
  public voucherForm: FormGroup;
  public submitted: boolean = false;
  private modified: boolean = false;
  private isLoading: boolean = false;
  public voucherEntries: VoucherEntry[] = [];
  public voucherId: number;
  public selectedFile: File | null = null;
  public formData: FormData;

  constructor(
    private financeService: FinanceService,
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private commonService: CommonService,
    private datePipe: DatePipe,
    private modalService: NgbModal,


  ) { }

  ngOnInit(): void {
    this.financeService.getVoucherType().subscribe((vouchers: string[]) => this.voucherTypes = vouchers);
    this.setVoucher(this.voucher);
    if (this.voucherForm.value.id) {
      this.getVoucherEntries();
    }
  }
  public setVoucher(voucher?: Voucher) {
    if (!voucher)
      voucher = {
        id: null,
        date: '',
        invoiceNo: '',
        voucherNo: '',
        narration: '',
        byStateCode: '',
        toStateCode: '',
        disabled: false,
        voucherType: '',
        voucherStatus: 'DRAFT',
        taxInclusive: false
      } as Voucher;

    this.voucherForm = this.fb.group({
      id: this.fb.control(voucher?.id || null),
      invoiceNo: this.fb.control(voucher.invoiceNo),
      voucherNo: this.fb.control(voucher.voucherNo),
      label: this.fb.control(voucher.narration),
      date: this.fb.control(this.datePipe.transform(voucher?.date || new Date(), 'yyyy-MM-dd'), [Validators.required]),
      byStateCode: this.fb.control(voucher.byStateCode),
      toStateCode: this.fb.control(voucher.toStateCode),
      narration: this.fb.control(voucher.narration, [Validators.required]),
      disabled: this.fb.control(voucher.disabled),
      voucherStatus: this.fb.control(voucher.voucherStatus),
      voucherType: this.fb.control(voucher.voucherType || null, [Validators.required]),
    });
    this.applyVoucherTypeValidators(voucher.voucherType);
    this.voucherForm.get('voucherType')?.valueChanges.subscribe(voucherType => {
      this.applyVoucherTypeValidators(voucherType);
      this.modified = true;
    });
  }

  private applyVoucherTypeValidators(voucherType: string) {
    const byStateControl = this.voucherForm.get('byStateCode');
    const toStateControl = this.voucherForm.get('toStateCode');
    if (voucherType === 'PURCHASE' || voucherType === 'SALES') {
      byStateControl?.setValidators([Validators.required, Validators.pattern(RegExp.stateCode)]);
      toStateControl?.setValidators([Validators.required, Validators.pattern(RegExp.stateCode)]);
    } else {
      byStateControl?.clearValidators();
      toStateControl?.clearValidators();
      byStateControl?.setValue('');
      toStateControl?.setValue('');
    }
    byStateControl?.updateValueAndValidity();
    toStateControl?.updateValueAndValidity();
  }

  get v() {
    return this.voucherForm.controls;
  }

  public getVoucher() {
    this.financeService.getVoucher(this.voucherForm.value.id).subscribe({
      next: (voucher: Voucher) => {
        if (voucher) {
          this.setVoucher(voucher);
          this.getVoucherEntries();
        } else {
          this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.commonService.setToastr(500, error?.error?.name || error.error);
      }
    }); 
  }

  public saveVoucher(inProgress?: any) {
    this.submitted = true;
    if (this.voucherForm.valid) {
      const voucher: Voucher = this.voucherForm.value;
      inProgress.value = true;
      if (voucher.id) {
        this.financeService.updateVoucher(voucher.id, voucher).subscribe({
          next: (response: Message) => {
            if (response) {
              inProgress.value = false; 
              this.passEntry.emit(response);
              this.commonService.setToastr(200, response.text);
              this.isLoading = false;
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      } else {
        this.financeService.createVoucher(voucher).subscribe({
          next: (response: Message) => {
            if (response) {
              inProgress.value = false;
              this.voucherForm.get('id')?.setValue(parseInt(response.payload));
              this.passEntry.emit(response);
              this.commonService.setToastr(200, response.text);
              this.getVoucher();
              this.isLoading = false;
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      }
    }
  }

  //TRANSACTION ENTRIES

  private getVoucherEntries() {
    this.financeService.getVoucherEntries(this.voucherForm.value.id).subscribe({
      next: (voucherEntries: VoucherEntry[]) => {
        this.voucherEntries = voucherEntries.sort((a, b) => a.id - b.id);
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.commonService.setToastr(500, error?.error?.name || error.error);
      }
    });
  }

  public upsertVoucherEntry(voucherEntries?: VoucherEntry) {
    const modalRef = this.modalService.open(VoucherEntriesComponent, { size: 'lg', backdrop: 'static', windowClass: 'child-modal' });
    modalRef.componentInstance.voucherEntry = voucherEntries;
    modalRef.componentInstance.voucherId = this.voucherForm.value.id;
    modalRef.componentInstance.voucher = this.voucher;
    modalRef.componentInstance.entries = this.voucherEntries;
    modalRef.result.then((response: Message) => {
      if (!response.failed) {
        this.getVoucherEntries();
      }
    });
  }

  public triggerFileInput(fileInputElement: HTMLInputElement) {
    fileInputElement.click();
  }

  public onFileSelected(event: Event, voucherId: number): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.uploadVoucherFile(voucherId);
    }
  }

  public uploadVoucherFile(voucherId: number): void {
    if (this.selectedFile) {
      let formData = new FormData();
      formData.append("file", this.selectedFile);
      this.financeService.uploadVoucherFile(voucherId, formData).subscribe({
        next: (response: Message) => {
          if (!response.failed) { 
            this.commonService.setToastr(200, response.text);
          } else {
            this.commonService.setToastr(400, response.text);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.commonService.setToastr(500, "Error uploading file");
        },
      });
    } else {
      this.commonService.setToastr(400, "No file selected");
    }
  }

}
